import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import { PortfolioItem } from '../types/content';
type PortfolioGridProps = {
  items: PortfolioItem[];
};
export function PortfolioGrid({
  items
}: PortfolioGridProps) {
  const [active, setActive] = useState<number | null>(null);
  const close = useCallback(() => setActive(null), []);
  const step = useCallback((dir: 1 | -1) => setActive(i => i === null ? i : (i + dir + items.length) % items.length), [items.length]);
  useEffect(() => {
    if (active === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [active, close, step]);
  const current = active !== null ? items[active] : null;
  return <>
      <ul className="grid grid-cols-2 gap-1 sm:grid-cols-3 lg:grid-cols-4" data-id="element-180" data-mp="2f7sqmovxkktu">
        {items.map((item, index) => <li key={item.id} data-id="element-181" data-mp="2698fq9wm3fg6" data-mpi={index}>
            <button type="button" onClick={() => setActive(index)} className="group relative block aspect-square w-full overflow-hidden bg-ink-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white" aria-label={`View ${item.title}`} data-id="element-182" data-mp="268fflrs5ro70">
              <img src={item.image} alt={item.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.04]" data-id="element-183" data-mp="1niqqifazn1zk" />
              <span className="absolute inset-0 flex items-end bg-ink/0 p-4 text-left opacity-0 transition-[opacity,background-color] duration-200 ease-out group-hover:bg-ink/70 group-hover:opacity-100 group-focus-visible:bg-ink/70 group-focus-visible:opacity-100" data-id="element-184" data-mp="1dj8bmd9xq6ed">
                <span data-id="element-185" data-mp="kpll8k5tf3zu">
                  <span className="block font-slab text-sm font-bold uppercase leading-snug tracking-[0.12em]" data-id="element-186" data-mp="kqwngfjm43ou">{item.title}</span>
                  {item.artist && <span className="mt-1 block font-script text-sm italic text-ink-muted" data-id="element-187" data-mp="fp7aguctxfy4">by {item.artist}</span>}
                </span>
              </span>
            </button>
          </li>)}
      </ul>

      <AnimatePresence data-id="element-188" data-mp="13gmjt730wxiv">
        {current && <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} role="dialog" aria-modal="true" aria-label={current.title} onClick={close} data-id="element-189" data-mp="13fbgf8dhprsa">
            <button type="button" onClick={close} className="absolute right-4 top-4 p-2 hover:opacity-60" aria-label="Close" data-id="element-190" data-mp="1ynn4c0lc337m">
              <XIcon className="h-7 w-7" strokeWidth={1.5} data-id="element-191" data-mp="28o5sv8nuf7vv" />
            </button>
            <button type="button" onClick={e => {
          e.stopPropagation();
          step(-1);
        }} className="absolute left-2 top-1/2 -translate-y-1/2 p-3 hover:opacity-60 md:left-6" aria-label="Previous" data-id="element-192" data-mp="28m2thy0sw2eh">
              <ChevronLeftIcon className="h-8 w-8" strokeWidth={1.5} data-id="element-193" data-mp="2redqjb8rx64o" />
            </button>
            <motion.figure key={current.id} initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} className="max-w-3xl" onClick={e => e.stopPropagation()} data-id="element-194" data-mp="2rckh5o6s07sz">
              <img src={current.image} alt={current.title} className="max-h-[75vh] w-auto object-contain" data-id="element-195" data-mp="2sfrqd971su87" />
              <figcaption className="mt-4 text-center" data-id="element-196" data-mp="2ngertr6tdyps">
                <span className="block font-slab text-sm font-bold uppercase tracking-[0.15em]" data-id="element-197" data-mp="2njucd9j9bzyl">{current.title}</span>
                <span className="mt-1 block text-sm text-ink-muted" data-id="element-198" data-mp="2xfxbs7mq5khu">
                  {current.artist ? `By ${current.artist} · ` : ''}Trip Ink Tattoo Las Vegas · {active! + 1} / {items.length}
                </span>
              </figcaption>
            </motion.figure>
            <button type="button" onClick={e => {
          e.stopPropagation();
          step(1);
        }} className="absolute right-2 top-1/2 -translate-y-1/2 p-3 hover:opacity-60 md:right-6" aria-label="Next" data-id="element-199" data-mp="24ovthxnqj8u3">
              <ChevronRightIcon className="h-8 w-8" strokeWidth={1.5} data-id="element-200" data-mp="1pzptw8kzjglo" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </>;
}